/**
 * Hook para gestionar Web Push notifications.
 * Pide permiso al usuario, registra el service worker y envía la suscripción al backend.
 */
import { useEffect, useState, useCallback } from 'react'
import apiClient from '@/lib/apiClient'
import { useAuthStore } from '@/store/authStore'

export function usePushNotifications() {
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  )
  const [isSubscribed, setIsSubscribed] = useState(false)
  const { isAuthenticated } = useAuthStore()

  // Check current subscription status
  useEffect(() => {
    if (!isAuthenticated || !('serviceWorker' in navigator)) return

    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription()
      setIsSubscribed(!!sub)
    }).catch(() => {})
  }, [isAuthenticated])

  const subscribe = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('Push notifications not supported')
      return false
    }

    try {
      // 1. Request permission
      const perm = await Notification.requestPermission()
      setPermission(perm)
      if (perm !== 'granted') return false

      // 2. Register push service worker
      await navigator.serviceWorker.register('/sw-push.js', { scope: '/' })

      // 3. Get VAPID public key from backend
      const vapidRes = await apiClient.get('/notifications/push/vapid-key/')
      const publicKey = vapidRes.data.public_key
      if (!publicKey) {
        console.warn('VAPID public key not configured on server')
        return false
      }

      // 4. Subscribe to push
      const reg = await navigator.serviceWorker.ready
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      })

      // 5. Send subscription to backend
      const subJson = subscription.toJSON()
      await apiClient.post('/notifications/push/subscribe/', {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      })

      setIsSubscribed(true)
      return true
    } catch (err) {
      console.error('Push subscription error:', err)
      return false
    }
  }, [])

  const unsubscribe = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      if (sub) {
        await apiClient.delete('/notifications/push/subscribe/', {
          data: { endpoint: sub.endpoint },
        })
        await sub.unsubscribe()
      }
      setIsSubscribed(false)
    } catch (err) {
      console.error('Push unsubscribe error:', err)
    }
  }, [])

  return {
    permission,
    isSubscribed,
    isSupported: typeof Notification !== 'undefined' && 'PushManager' in window,
    subscribe,
    unsubscribe,
  }
}

// Helper: convert base64 VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}
